import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion as Motion } from "framer-motion";
import { toast } from "react-toastify";

import api from "@/utils/axios";
import { timeAgo } from "@/utils/timeAgo";
import cls from "./UserMemberPaymentSubmissionsSingle.module.scss";

const UserMemberPaymentSubmissionsSingle = () => {
  const { userId, memberPaymentSubmissionId } = useParams();
  const navigate = useNavigate();

  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const proofImageRef = useRef(null);

  const fetchSubmission = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const res = await api.get(
        `/v1/admin/members/payment-submission/${memberPaymentSubmissionId}`,
      );

      setSubmission(res.data);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message || "Failed to load payment submission.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [memberPaymentSubmissionId]);

  useEffect(() => {
    fetchSubmission();
  }, [fetchSubmission]);

  const goBack = () => {
    navigate(`/admin/members/single/payment-info/${userId}`);
  };

  const handleApprove = async () => {
    if (!submission) return;
    if (submission.status === "approved") {
      toast.info("This payment is already approved.");
      return;
    }
    if (submission.status === "decline") {
      toast.info("Declined payments cannot be approved.");
      return;
    }
    setShowApproveForm(true);
    setShowDeclineForm(false);
  };

  const handleApproveSubmit = async () => {
    if (!submission) return;
    if (submission.status === "approved") {
      toast.info("This payment is already approved.");
      return;
    }

    try {
      setActionLoading(true);
      await api.post(
        `/v1/admin/members/payment-submission/${memberPaymentSubmissionId}/approve`,
      );

      toast.success("Payment approved.");
      setShowDeclineForm(false);
      setDeclineReason("");
      setShowApproveForm(false);
      await fetchSubmission();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Failed to approve payment.";
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!submission) return;
    if (submission.status === "decline") {
      toast.info("This payment is already declined.");
      return;
    }
    setShowDeclineForm(true);
    setShowApproveForm(false);
  };

  const handleDeclineSubmit = async () => {
    if (!submission) return;
    if (submission.status === "decline") {
      toast.info("This payment is already declined.");
      return;
    }

    const trimmedReason = declineReason.trim();
    if (!trimmedReason) {
      toast.error("Please provide a reason for declining.");
      return;
    }

    try {
      setActionLoading(true);
      await api.post(
        `/v1/admin/members/payment-submission/${memberPaymentSubmissionId}/decline`,
        { reason: trimmedReason },
      );

      toast.success("Payment declined.");
      setShowDeclineForm(false);
      setDeclineReason("");
      await fetchSubmission();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message || "Failed to decline payment.";
      toast.error(msg);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewImage = () => {
    const url = submission?.proofImage?.url;
    if (!url) {
      toast.error("No proof image available.");
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleFullscreenImage = async () => {
    const url = submission?.proofImage?.url;
    if (!url) {
      toast.error("No proof image available.");
      return;
    }

    const imageEl = proofImageRef.current;
    if (imageEl?.requestFullscreen) {
      try {
        await imageEl.requestFullscreen();
        return;
      } catch (err) {
        console.error("Fullscreen request failed:", err);
      }
    }

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const renderStatusBadge = (status) => {
    if (status === "approved") {
      return (
        <span className={`${cls.badge} ${cls.badgeApproved}`}>Approved</span>
      );
    }
    if (status === "decline") {
      return (
        <span className={`${cls.badge} ${cls.badgeDecline}`}>Declined</span>
      );
    }
    return <span className={`${cls.badge} ${cls.badgePending}`}>Pending</span>;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value);
    }
  };

  const poId = submission?.paymentOption?.id;
  const poTitle =
    submission?.paymentOption?.title ||
    submission?.paymentOption?.name ||
    "Payment option";

  const createdAtText = submission?.createdAt
    ? timeAgo(submission.createdAt)
    : "-";

  return (
    <div className={cls.page}>
      {/* Header + back */}
      <div className={cls.headerRow}>
        <button type="button" onClick={goBack} className={cls.backBtn}>
          <span className={cls.backArrow}>&larr;</span>
          <span>Back to payment submissions</span>
        </button>

        <div className={cls.headerText}>
          <h1 className={cls.title}>Member Payment Submission</h1>
          <p className={cls.subtitle}>
            Review the payment details and approve or decline this submission.
          </p>
        </div>
      </div>

      {/* Loading / Error states */}
      {loading && (
        <div className={cls.stateBox}>
          <div className={cls.loader} />
          <span>Loading payment details...</span>
        </div>
      )}

      {!loading && error && (
        <div className={cls.stateBoxError}>
          <p>{error}</p>
        </div>
      )}

      {!loading && !error && submission && (
        <>
          {/* Status + actions */}
          <Motion.section
            className={cls.topBar}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className={cls.topLeft}>
              <span className={cls.label}>Current Status:</span>
              {renderStatusBadge(submission.status)}
            </div>

            <div className={cls.actions}>
              <button
                type="button"
                className={cls.btnApprove}
                onClick={handleApprove}
                disabled={
                  actionLoading ||
                  submission.status === "approved" ||
                  submission.status === "decline"
                }
              >
                Approve
              </button>
              <button
                type="button"
                className={cls.btnDecline}
                onClick={handleDecline}
                disabled={
                  actionLoading ||
                  submission.status === "decline" ||
                  submission.status === "approved"
                }
              >
                Decline
              </button>
            </div>
          </Motion.section>

          {showDeclineForm && submission.status === "pending" && (
            <Motion.section
              className={cls.declinePanel}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className={cls.declineHeader}>
                <h3>Why are you declining this refill?</h3>
                <p>
                  Please provide a clear reason that will be shared with the
                  member.
                </p>
              </div>
              <textarea
                className={cls.declineInput}
                rows={4}
                value={declineReason}
                onChange={(event) => setDeclineReason(event.target.value)}
                placeholder="Add the decline reason..."
              />
              <div className={cls.declineActions}>
                <button
                  type="button"
                  className={cls.btnGhost}
                  onClick={() => {
                    setShowDeclineForm(false);
                    setDeclineReason("");
                  }}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={cls.btnDeclineSubmit}
                  onClick={handleDeclineSubmit}
                  disabled={actionLoading}
                >
                  Submit Decline
                </button>
              </div>
            </Motion.section>
          )}

          {showApproveForm && submission.status === "pending" && (
            <Motion.section
              className={cls.approvePanel}
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className={cls.approveHeader}>
                <h3>Approve this payment submission?</h3>
                <p>
                  This will credit the member wallet and mark the submission as
                  approved.
                </p>
              </div>
              <div className={cls.approveActions}>
                <button
                  type="button"
                  className={cls.btnGhost}
                  onClick={() => {
                    setShowApproveForm(false);
                  }}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={cls.btnApproveSubmit}
                  onClick={handleApproveSubmit}
                  disabled={actionLoading}
                >
                  Yes, Approve
                </button>
              </div>
            </Motion.section>
          )}

          {/* SINGLE COLUMN */}
          <Motion.section
            className={cls.mainCard}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Payment option title link */}
            <div className={cls.sectionTop}>
              <div className={cls.poBlock}>
                <span className={cls.poLabel}>Payment Option</span>

                {poId ? (
                  <Link
                    to={`/admin/finance/payment-options/${poId}`}
                    className={cls.poLink}
                    title="Open payment option"
                  >
                    {poTitle}
                    <span className={cls.poLinkHint}>View</span>
                  </Link>
                ) : (
                  <span className={cls.poPlain}>{poTitle}</span>
                )}
              </div>

              <div className={cls.metaPills}>
                <span className={cls.pill}>
                  <span className={cls.pillK}>Submission</span>
                  <span className={cls.pillV}>{submission.id}</span>
                </span>
                <span className={cls.pill}>
                  <span className={cls.pillK}>Created</span>
                  <span className={cls.pillV}>{createdAtText}</span>
                </span>
              </div>
            </div>

            <div className={cls.divider} />

            {/* Avatar + username + createdAt */}
            <div className={cls.memberRow}>
              <div className={cls.avatarStack}>
                {submission.member?.avatar?.url ? (
                  <img
                    src={submission.member.avatar.url}
                    alt={`${submission.user?.username || "Member"} avatar`}
                    className={cls.avatarImage}
                  />
                ) : (
                  <div className={cls.avatarFallback}>
                    {submission.user?.username?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                )}
              </div>

              <div className={cls.memberDetails}>
                <div className={cls.memberNameRow}>
                  <span className={cls.memberName}>
                    {submission.user?.username || "Unknown user"}
                  </span>
                  <span className={cls.memberSub}>
                    Created {createdAtText}
                    <span className={cls.dot}>•</span>
                    {formatDate(submission.createdAt)}
                  </span>
                </div>

                <div className={cls.memberMiniMeta}>
                  {submission.member ? (
                    <>
                      <span className={cls.miniChip}>
                        Member #{submission.member.id}
                      </span>
                      <span className={cls.miniChip}>
                        {submission.member.memberType} /{" "}
                        {submission.member.status}
                      </span>
                    </>
                  ) : (
                    <span className={cls.miniChip}>
                      No member profile attached
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className={cls.divider} />

            {/* Submission details (single column, nice rows) */}
            <h3 className={cls.sectionTitle}>Submission Details</h3>

            <div className={cls.kvGrid}>
              <div className={cls.kvItem}>
                <span className={cls.k}>Transfer Date</span>
                <span className={cls.v}>
                  {formatDate(submission.transferDate)}
                </span>
              </div>

              <div className={cls.kvItem}>
                <span className={cls.k}>Amount (CAD)</span>
                <span className={cls.v}>
                  {submission.amountCad != null ? submission.amountCad : "-"}
                </span>
              </div>

              <div className={cls.kvItem}>
                <span className={cls.k}>Amount (Noty)</span>
                <span className={cls.v}>
                  {submission.amountNoty != null ? submission.amountNoty : "-"}
                </span>
              </div>

              <div className={cls.kvItem}>
                <span className={cls.k}>Updated At</span>
                <span className={cls.v}>
                  {formatDate(submission.updatedAt)}
                </span>
              </div>
            </div>

            <div className={cls.divider} />

            {/* Approved by block (kept same logic, better look) */}
            {submission.status === "approved" && (
              <>
                <h3 className={cls.sectionTitle}>Approval</h3>
                <div className={cls.kvGrid}>
                  <div className={cls.kvItem}>
                    <span className={cls.k}>Approved By</span>
                    <span className={cls.v}>
                      {submission.approvedBy?.username || "-"}
                    </span>
                  </div>
                  <div className={cls.kvItem}>
                    <span className={cls.k}>Approved At</span>
                    <span className={cls.v}>
                      {formatDate(submission.approvedAt)}
                    </span>
                  </div>
                </div>
                <div className={cls.divider} />
              </>
            )}

            {/* Proof of payment */}
            <h3 className={cls.sectionTitle}>Proof of Payment</h3>

            <div className={cls.proofHeader}>
              <div className={cls.rowVertical}>
                <span className={cls.label}>Proof Image Key</span>
                {submission.proofImageKey ? (
                  <code className={cls.code}>{submission.proofImageKey}</code>
                ) : (
                  <span className={cls.valueMuted}>
                    No proof image key provided
                  </span>
                )}
              </div>

              {submission.proofImage?.url && (
                <div className={cls.proofActions}>
                  <button
                    type="button"
                    className={cls.iconBtn}
                    onClick={handleViewImage}
                    disabled={!submission.proofImage.url}
                    title="Open image"
                    aria-label="Open image"
                  >
                    ↗
                  </button>
                  <button
                    type="button"
                    className={cls.iconBtn}
                    onClick={handleFullscreenImage}
                    disabled={!submission.proofImage.url}
                    title="Fullscreen"
                    aria-label="Fullscreen"
                  >
                    ⛶
                  </button>
                </div>
              )}
            </div>

            {submission.proofImage && (
              <div className={cls.proofImageBlock}>
                {submission.proofImage.url ? (
                  <div className={cls.proofImageWrapper}>
                    <img
                      src={submission.proofImage.url}
                      alt="Payment proof"
                      className={cls.proofImage}
                      ref={proofImageRef}
                    />
                  </div>
                ) : (
                  <span className={cls.valueMuted}>
                    Image URL is not available.
                  </span>
                )}
              </div>
            )}

            <p className={cls.helperText}>
              Use the proof image key to locate the upload in your S3 bucket or
              storage viewer if needed.
            </p>

            <div className={cls.divider} />

            {/* Kept as-is (visible details only styled) */}
            <h3 className={cls.sectionTitle}>Audit</h3>

            <div className={cls.kvGrid}>
              <div className={cls.kvItem}>
                <span className={cls.k}>Created By</span>
                <span className={cls.v}>
                  {submission.createdBy?.username || "-"}
                </span>
              </div>

              <div className={cls.kvItem}>
                <span className={cls.k}>Updated By</span>
                <span className={cls.v}>
                  {submission.updatedBy?.username || "-"}
                </span>
              </div>

              <div className={cls.kvItemFull}>
                <span className={cls.k}>IP Address</span>
                <span className={cls.v}>{submission.ipAddress || "-"}</span>
              </div>
            </div>
          </Motion.section>
        </>
      )}
    </div>
  );
};

export default UserMemberPaymentSubmissionsSingle;
